import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import { IconTrash } from '@tabler/icons-react'

import { apiErrorMessage } from '@/api'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Pill } from '@/components/pills/Pill'
import ConfirmModal from '@/components/layout/modals/ConfirmModal'
import { toast } from '@/components/toasts/toast.helpers'
import { formatSummaryTableContent, formatTime } from '@/utils/helpers'

import { ALL_PLAYBOOKS, formatPlaybookScope, isScopeUnused } from './AiAnswers.helpers'
import { AiAnswersEditAnswer } from './AiAnswersEditAnswer'
import { deleteVerifiedAnswer } from './verifiedAnswers.api'

const NO_ANSWERS_HEADER = 'No verified answers yet'
const NO_ANSWERS_TEXT = 'Add an answer, or answer a knowledge gap.'
const UNAVAILABLE_HEADER = "Verified answers couldn't be loaded"
const UNAVAILABLE_TEXT = 'Refresh to try again.'
const DELETE_FAILED = "The answer couldn't be deleted. Try again."

/**
 * @import import { AiAnswersVerifiedAnswers } from '@/views/AiAnswers/AiAnswersVerifiedAnswers'
 * @param {object} props
 * @param {object} [props.verifiedAnswers] The org's verified answers; null while loading or after a failed load
 * @param {boolean} props.loading Shows the table loader while the answers fetch
 * @param {object} props.playbooks The org's playbook options and their load state
 * @param {boolean} props.canAnswer Offers edit and delete on each answer
 * @param {number} props.organizationId The org the answers belong to
 * @param {Function} props.onChange Called after an answer is edited or deleted
 */
export const AiAnswersVerifiedAnswers = ({
  verifiedAnswers,
  loading,
  playbooks,
  canAnswer,
  organizationId,
  onChange,
}) => {
  const dispatch = useDispatch()
  const [deleting, setDeleting] = useState(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const unavailable = !loading && isNil(verifiedAnswers)

  const columns = [
    { accessor: 'updated_by_name', label: 'User', isSearchable: true, collapsing: true },
    {
      accessor: 'updated_at',
      label: 'Date',
      collapsing: true,
      format: (value) => formatTime(value),
    },
    {
      accessor: 'question',
      label: 'Question',
      isSearchable: true,
      format: (value) => <div>{formatSummaryTableContent(value)}</div>,
    },
    {
      accessor: 'answer',
      label: 'Answer',
      isSearchable: true,
      format: (value) => <div>{formatSummaryTableContent(value)}</div>,
    },
    {
      accessor: 'config_cids',
      label: 'Playbooks',
      format: (value) => {
        if (isEmpty(value)) {
          return <span className="muted-text">{ALL_PLAYBOOKS}</span>
        }

        return (
          <div className="flex-align-center small-gap">
            {formatPlaybookScope(value, playbooks)}
            {isScopeUnused(value, playbooks) && (
              <Pill warning xs>
                Not in use
              </Pill>
            )}
          </div>
        )
      },
    },
  ]

  const actions = canAnswer
    ? [
        {
          label: 'Edit',
          trigger: (row) => (
            <AiAnswersEditAnswer
              verifiedAnswer={row}
              verifiedAnswers={verifiedAnswers}
              playbooks={playbooks}
              organizationId={organizationId}
              onSaved={onChange}
            />
          ),
        },
        {
          label: 'Delete',
          trigger: (row) => (
            <Button
              basic
              small
              square
              aria-label="Delete answer"
              tooltip="Delete"
              onClick={() => {
                setDeleteError(null)
                setDeleting(row)
              }}
            >
              <IconTrash />
            </Button>
          ),
        },
      ]
    : []

  const handleDelete = async () => {
    setDeleteBusy(true)
    setDeleteError(null)

    try {
      await dispatch(deleteVerifiedAnswer({ organizationId, answerId: deleting.id }))
    } catch (error) {
      setDeleteBusy(false)
      setDeleteError(apiErrorMessage(error, DELETE_FAILED))
      return
    }

    toast.success('Verified answer deleted')
    setDeleteBusy(false)
    setDeleting(null)
    onChange()
  }

  return (
    <div data-testid="ai-answers-verified-answers">
      <AdvancedTable
        loading={loading}
        rows={isNil(verifiedAnswers) ? [] : verifiedAnswers.items}
        columns={columns}
        actions={actions}
        pagination
        defaultOrderBy="updated_at"
        defaultOrder="desc"
        itemName="answer"
        noDataHeader={unavailable ? UNAVAILABLE_HEADER : NO_ANSWERS_HEADER}
        noDataText={unavailable ? UNAVAILABLE_TEXT : NO_ANSWERS_TEXT}
      />
      <ConfirmModal
        show={!isNil(deleting)}
        onClose={() => setDeleting(null)}
        title="Delete verified answer"
        confirmLabel="Delete"
        busyLabel="Deleting..."
        busy={deleteBusy}
        destructive
        onConfirm={handleDelete}
        testIdPrefix="delete-verified-answer"
      >
        <p>
          AgentGPT stops using this answer, and the questions it answered go back to Knowledge gaps.
        </p>
        {deleteError && <ErrorMessage content={deleteError} />}
      </ConfirmModal>
    </div>
  )
}
